import { Injectable, Signal, signal } from '@angular/core';
import { intersectionBy } from 'lodash';

import { EnrollmentGroup, Parcel, ParcelEnrollmentGroups } from '@shared/models';
import { AppConfigService } from '@core/api/app-config.service';


@Injectable()
export class ParcelSelectionStore {
  maxParcels: number = 25;
  dirty: boolean = false;
  locked: boolean = false;
  availableEnrollmentGroups: EnrollmentGroup[] = [];

  private readonly _parcels = signal<Parcel[]>([]);
  readonly parcels: Signal<Parcel[]> = this._parcels.asReadonly();

  constructor(readonly appConfigService: AppConfigService) {
    this.maxParcels = this.appConfigService.getFloat('max_parcels_per_site', 25);
  }

  /**
   * Add a parcel to the selection.
   *
   * Returns `true` if the parcel was added, `false` if it was rejected because the
   * selection is at capacity. Adding a parcel whose `llUuid` is already in the
   * selection is a no-op (returns `true` — the desired end state is unchanged).
   */
  add(parcel: Parcel): boolean {
    if (!parcel) {
      return false;
    }
    const current = this._parcels();
    if (current.some(p => p.llUuid === parcel.llUuid)) {
      return true;
    }
    if (current.length >= this.maxParcels) {
      return false;
    }
    this._parcels.set([...current, parcel]);
    this.dirty = true;
    return true;
  }

  /**
   * Remove a parcel by its `llUuid`. Missing parcels are silently ignored.
   */
  remove(llUuid: string): void {
    const current = this._parcels();
    const next = current.filter(p => p.llUuid !== llUuid);
    if (next.length !== current.length) {
      this._parcels.set(next);
      this.dirty = true;
    }
  }

  /**
   * Replace the entire selection. Used for initial hydration from the active site.
   * A defensive copy is stored so external mutations don't leak into the signal.
   *
   * Hydration is not a user-initiated change, so the dirty flag is reset here.
   */
  setAll(parcels: Parcel[]): void {
    this._parcels.set(parcels ? [...parcels] : []);
    this.dirty = false;
  }

  /**
   * Given the per-parcel enrollment groups for every currently-selected parcel,
   * return the enrollment groups that are available to *all* of them (intersection
   * by `id`).
   *
   * Behavior:
   * - Empty input → empty result.
   * - Single-parcel input → that parcel's enrollment groups verbatim.
   * - Multi-parcel input → intersection by `id`, preserving order/instances from
   *   the first parcel's list.
   *
   * Pure; safe to test in isolation.
   */
  computeAvailableEnrollmentGroups(allEnrollmentGroups: ParcelEnrollmentGroups[]) {
    if (!allEnrollmentGroups || allEnrollmentGroups.length === 0) {
      this.availableEnrollmentGroups = [];
      return;
    }
    const groups = allEnrollmentGroups.map(c => c.enrollmentGroups ?? []);
    if (groups.length === 1) {
      this.availableEnrollmentGroups = groups[0];
      return;
    }
    this.availableEnrollmentGroups = groups.slice(1).reduce((acc, next) => intersectionBy(acc, next, 'id'), groups[0]);
  }
}
